import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const tireSelectionFactors = [
    {
        id: 'size',
        title: '1. Start with the Right Size',
        text: 'The first step is getting the size correct. Check your driver-side door jamb or owner’s manual for the specific tire measurements your car requires. Installing the wrong size can affect speedometer accuracy and handling stability.'
    },
    {
        id: 'type',
        title: '2. Choosing the Right Tire Type',
        text: 'Not all tires are the same. All-Season tires are great for general use, but Touring tires offer better comfort and lower noise for highway commuters. Performance tires provide sharper grip, while All-Terrain tires are essential for mixed on-and-off-road use.'
    },
    {
        id: 'climate',
        title: '3. Match Tires to Climate and Roads',
        text: 'Your environment dictates your tire needs. If you live in a rainy area, look for tires with high hydroplaning resistance. If you face harsh winters, dedicated snow tires provide significantly more traction than even the best all-season tires.'
    },
    {
        id: 'budget',
        title: '4. Price vs Long-Term Value',
        text: 'The cheapest tire isn’t always the best value. Look at the treadwear warranty and fuel efficiency ratings. A slightly more expensive tire that lasts 20,000 miles longer and saves fuel can be the cheaper option over its lifetime.'
    }
];

const faqs = [
    {
        question: 'Can I choose a different tire size than stock?',
        answer: 'You can, but it should be done carefully. Small changes are common for aesthetics or performance, but large changes can interfere with your car’s suspension, brakes, and safety systems.'
    },
    {
        question: 'Are all-season tires enough for winter?',
        answer: 'In mild climates with occasional light snow, yes. However, if temperatures consistently drop below 45°F (7°C), winter tires are much safer as their rubber remains flexible and provides better grip.'
    },
    {
        question: 'Is the cheapest tire set a bad idea?',
        answer: 'Budget tires meet basic safety standards, but they often wear out faster, produce more road noise, and have longer stopping distances on wet roads compared to premium brands.'
    },
    {
        question: 'How often should I rotate my tires?',
        answer: 'Most manufacturers recommend every 5,000 to 7,500 miles. Regular rotation prevents uneven wear, extending the life of your tires and keeping your car’s handling balanced.'
    }
];

const Blog19 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 19;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            How to Choose the Right Tires for Your Car (Complete Guide)
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            A lot of drivers buy tires only when the old ones are worn out and the shop says it's time. Don't rush a decision that affects your safety and comfort.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 7 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/19_1.jpeg"
                            alt="Choosing tires for different driving conditions"
                            title="How to Choose the Right Tires for Your Car"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: choosing_the_right_tires.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#size" className="text-gray-900 no-underline hover:underline">Start with the right size</a></li>
                            <li><a href="#type" className="text-gray-900 no-underline hover:underline">Choosing the right type</a></li>
                            <li><a href="#climate" className="text-gray-900 no-underline hover:underline">Climate and roads</a></li>
                            <li><a href="#budget" className="text-gray-900 no-underline hover:underline">Price vs value</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            The right tire depends on how you drive, where you drive, and what you actually expect from the car day to day. Rushing a tire purchase often leads to higher road noise, faster wear, or poor traction when you need it most.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Tire shopping gets much easier once the size, load rating, and speed rating are confirmed before you start comparing brands. The best tire matches your car, climate, and driving habits.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>How to choose?</strong><br />
                            Match three things: the manufacturer\'s recommended size, your primary driving environment (highway vs city vs off-road), and your local climate (all-season vs winter).
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/19_2.jpeg"
                            alt="Checking tire condition and tire type before purchase"
                            title="Comparing tire options before buying"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: tire_type_comparison.png
                        </figcaption>
                    </figure>

                    {tireSelectionFactors.map((item) => (
                        <section key={item.id} id={item.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {item.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{item.text}</p>
                        </section>
                    ))}

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Choosing the right tires affects safety, comfort, and fuel efficiency. CarFido Assistant helps you find the perfect match for your specific vehicle and usage.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Recommend the right tire category based on your actual driving history and climate</li>
                                <li>Store your purchase records, including brand, size, and installation mileage</li>
                                <li>Track your rotation schedule and send reminders to extend tire life</li>
                                <li>Help you project when you'll need replacements based on your average annual mileage</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            The right tire is the one that fits your car and your real use, not just the one with the lowest sticker price. Tire choice affects comfort, grip, braking, and noise more than many drivers realize.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Take the time to start with the correct size, match the category to your roads and weather, and think about long-term maintenance. A little research now saves money and frustration later.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#size" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Correct size</a></li>
                            <li><a href="#type" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Tire type</a></li>
                            <li><a href="#climate" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Climate/roads</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog19;
